import { useId, useState, type ChangeEvent, type ReactNode } from 'react';

import { useI18n, SUPPORTED_LOCALES, type Locale } from '../i18n';
import { HelpIcon } from './feedback';

/**
 * Bedienelemente.
 *
 * Alles hier ist bewusst schlicht und benutzt echte HTML-Elemente: Buttons
 * sind Buttons, Eingaben haben Labels, der Schalter meldet seinen Zustand über
 * `aria-checked`. Das ist die Grundlage dafür, dass die Oberfläche auch mit
 * der Tastatur bedienbar bleibt.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  size = 'normal',
  block = false,
  type = 'button',
  disabled,
  onClick,
  children,
  ...rest
}: {
  variant?: ButtonVariant;
  size?: 'normal' | 'gross';
  block?: boolean;
  type?: 'button' | 'submit';
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  'aria-label'?: string;
  'aria-haspopup'?: boolean;
}): JSX.Element {
  const klassen = ['btn', 'btn-' + variant];
  if (size === 'gross') {
    klassen.push('btn-gross');
  }
  if (block) {
    klassen.push('btn-block');
  }

  return (
    <button type={type} className={klassen.join(' ')} disabled={disabled} onClick={onClick} {...rest}>
      {children}
    </button>
  );
}

/**
 * Textbutton für Nebensächliches - niemals für die Hauptaktion einer Seite
 * und niemals für etwas, das Daten verändert.
 */
export function LinkButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <button type="button" className="link-button" disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * Beschriftetes Formularfeld.
 *
 * Beschriftung und Eingabe gehören immer zusammen: mit `htmlFor` über die
 * Kennung, ohne `htmlFor` dadurch, dass das Feld im `<label>` steht. Beides
 * führt dazu, dass ein Klick auf die Beschriftung im Feld landet und
 * Hilfsmittel die richtige Bezeichnung vorlesen.
 */
export function FormField({
  label,
  hint,
  error,
  help,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  /** Kurze Erklärung als `?` neben der Beschriftung. */
  help?: string;
  htmlFor?: string;
  children: ReactNode;
}): JSX.Element {
  const beschriftung = (
    <span className="field-label">
      {label}
      {help ? <HelpIcon text={help} label={label} /> : null}
    </span>
  );
  const rest = (
    <>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-fehler">{error}</span> : null}
    </>
  );

  if (htmlFor) {
    return (
      <div className="field">
        <label className="field-label" htmlFor={htmlFor}>
          {label}
          {help ? <HelpIcon text={help} label={label} /> : null}
        </label>
        {rest}
      </div>
    );
  }

  return (
    <label className="field">
      {beschriftung}
      {rest}
    </label>
  );
}

/** Altbewährte Kurzform: Label und Eingabe in einem `<label>`. */
export function Field({
  label,
  hint,
  help,
  children,
}: {
  label: string;
  hint?: string;
  help?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {help ? <HelpIcon text={help} label={label} /> : null}
      </span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Input({
  value,
  onChange,
  type = 'text',
  id,
  placeholder,
  disabled,
  autoComplete,
}: {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | 'search' | 'date' | 'number';
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  autoComplete?: string;
}): JSX.Element {
  return (
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete={autoComplete}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    />
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  id,
  disabled,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  id?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  help,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  help?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="field checkbox">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="field-label">
        {label}
        {help ? <HelpIcon text={help} label={label} /> : null}
      </span>
    </label>
  );
}

/**
 * Schalter für Einstellungen, die sofort wirken.
 *
 * Für Formulare, die erst beim Speichern greifen, ist die Checkbox das
 * ehrlichere Element.
 */
export function Toggle({
  checked,
  onChange,
  label,
  help,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  help?: string;
  disabled?: boolean;
}): JSX.Element {
  const id = useId();

  return (
    <div className="field toggle-zeile">
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-label={label}
        className="toggle"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
      <label className="field-label" htmlFor={id}>
        {label}
        {help ? <HelpIcon text={help} label={label} /> : null}
      </label>
    </div>
  );
}

export function Slider({
  value,
  min,
  max,
  onChange,
  id,
  disabled,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  id?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="slider-zeile">
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <output className="slider-wert">{value}</output>
    </div>
  );
}

/**
 * Passworteingabe mit Umschalter sichtbar/unsichtbar
 * (01_roles_and_accounts.md). Ein gespeichertes Passwort wird nie angezeigt -
 * dieses Feld ist immer leer, bis jemand etwas eintippt.
 */
export function PasswordInput({
  value,
  onChange,
  autoComplete = 'current-password',
  id,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  id?: string;
  placeholder?: string;
}): JSX.Element {
  const [visible, setVisible] = useState(false);
  const { t } = useI18n();

  return (
    <span className="password-input">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        className="link-button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? t('action.hidePassword') : t('action.showPassword')}
      >
        {visible ? t('action.hidePassword') : t('action.showPassword')}
      </button>
    </span>
  );
}

/** Sprachumschalter - immer oben rechts neben dem Abmelden. */
export function LanguageSwitcher({ onChange }: { onChange?: (locale: Locale) => void }): JSX.Element {
  const { locale, setLocale, t } = useI18n();

  return (
    <div className="language-switcher" role="group" aria-label={t('nav.language')}>
      {SUPPORTED_LOCALES.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={option === locale}
          className={option === locale ? 'language-active' : undefined}
          onClick={() => {
            setLocale(option);
            onChange?.(option);
          }}
        >
          {option.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
